/* Lab 1.6 · 模仿学习：示范多少才够？

   Everything on screen is measured (data.json, from make_data.py on the L5 capstone): the curve is
   success rate against the number of scripted demonstrations, five training runs per point, for
   demonstrations spread over the whole cup range and for demonstrations all in one 1 x 1 cm patch;
   the two replays are recorded evaluation episodes from the same starting position.

   The scripted take (17 s), in two slices for episode 1.6:
     0–8   the demo count sweeps 5 → 100 twice: first all in one place (violet), then varied (green)
     8–17  two replays: 10 demonstrations in one place picks the cup up, stalls halfway and drops it
           (amber); 100 varied demonstrations carries it straight into the bin (green) */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, clamp, span, ease, el } from "../kit/stage";
import { Chart } from "../kit/chart";
import { localizePage, tr, type Dict } from "../kit/i18n";
import DATA from "./data.json";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

type Kind = "diverse" | "narrow";
type Which = "few" | "many";
interface Point { demos: number; runs: number[]; mean: number; wilson95: [number, number] }
interface Replay { label: string; seed: number; success: boolean; hz: number; gripper: number[][]; cup: number[][] }
const curve = DATA.curve as Record<Kind, Point[]>;
const replays = DATA.replays as unknown as Record<Which, Replay>;
const S = DATA.scene;
const COUNTS = curve.diverse.map(p => p.demos);

// --- the scripted take
interface Shot { idx: number; kind: Kind; which: Which; time: number; sweep: boolean }
function scripted(t: number): Shot {
  if (t < 8) {
    const narrow = t < 4, k = span(t % 4, .3, 3.6);
    return { idx: Math.round(ease(k) * 6), kind: narrow ? "narrow" : "diverse", which: narrow ? "few" : "many", time: 0, sweep: true };
  }
  if (t < 13) return { idx: 1, kind: "narrow", which: "few", time: span(t, 8.3, 12.8) * 4.6, sweep: false };
  return { idx: 6, kind: "diverse", which: "many", time: span(t, 13.3, 16.6) * 1.7, sweep: false };
}

// --- scene: the capstone's table, bin and cup; the gripper replays the recorded episode
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
// the table sits right of the chart panel; the recorded take's panel is larger (index.html), so the table moves further right
camera.setViewOffset(1920, 1080, recorded ? -470 : -380, -60, 1920, 1080);
floor(scene);
const W = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, z, -y);   // capstone world (z up) to scene (y up)
const metal = (color: number, metalness = .6, roughness = .45) => new THREE.MeshStandardMaterial({ color, metalness, roughness });
const glow = (color: number, k = 1.4) => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: k, roughness: .4 });
{
  const table = new THREE.Mesh(new THREE.BoxGeometry(.5, S.table_top, .4), metal(0x1b2230, .2, .8));
  table.position.copy(W(0, 0, S.table_top / 2));
  table.receiveShadow = table.castShadow = true;
  scene.add(table);
  const [bx, by] = S.bin_xy, h = S.bin_rim_z - S.table_top, half = S.bin_half + .006;
  const binMat = metal(0x2c3444, .5, .5);
  const bottom = new THREE.Mesh(new THREE.BoxGeometry(half * 2, .01, half * 2), binMat);
  bottom.position.copy(W(bx, by, S.table_top + .005));
  scene.add(bottom);
  for (const [dx, dy, sx, sy] of [[-half, 0, .012, half * 2], [half, 0, .012, half * 2], [0, -half, half * 2, .012], [0, half, half * 2, .012]]) {
    const wall = new THREE.Mesh(new THREE.BoxGeometry(sx, h, sy), binMat);
    wall.position.copy(W(bx + dx, by + dy, S.table_top + h / 2));
    wall.castShadow = true;
    scene.add(wall);
  }
  const rim = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(half * 2, .001, half * 2)), new THREE.LineBasicMaterial({ color: C.see, transparent: true, opacity: .8 }));
  rim.position.copy(W(bx, by, S.bin_rim_z));
  scene.add(rim);
  // the patch the "one place" demonstrations came from, and the full range, on the table top
  const [[x0, x1], [y0, y1]] = DATA.protocol.full_range as number[][];
  const zone = (a: number, b: number, c: number, d: number, colour: number, o: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(b - a, d - c), new THREE.MeshBasicMaterial({ color: colour, transparent: true, opacity: o, depthWrite: false }));
    m.rotation.x = -Math.PI / 2;
    m.position.copy(W((a + b) / 2, (c + d) / 2, S.table_top + .0015));
    scene.add(m);
    return m;
  };
  zone(x0 - S.cup_r, x1 + S.cup_r, y0 - S.cup_r, y1 + S.cup_r, C.see, .08);
  const [[n0, n1], [m0, m1]] = DATA.protocol.narrow_patch as number[][];
  zone(n0 - .003, n1 + .003, m0 - .003, m1 + .003, C.think, .55);
}
const cupMat = new THREE.MeshStandardMaterial({ color: 0x2a5b8a, metalness: .2, roughness: .35 });
const cup = new THREE.Mesh(new THREE.CylinderGeometry(S.cup_r, S.cup_r, S.cup_hh * 2, 40), cupMat);
cup.castShadow = true;
scene.add(cup);
const gripper = new THREE.Group();
const fingers: THREE.Mesh[] = [];
{
  const palm = new THREE.Mesh(new THREE.BoxGeometry(.12, .02, .06), metal(0x566274, .8, .35));
  palm.position.y = .06;
  const rod = new THREE.Mesh(new THREE.CylinderGeometry(.008, .008, .3, 16), metal(0x3a4456));
  rod.position.y = .22;
  gripper.add(palm, rod);
  for (let i = 0; i < 2; i++) {
    const f = new THREE.Mesh(new THREE.BoxGeometry(.02, .07, .04), glow(C.act, .6));
    f.castShadow = true;
    gripper.add(f);
    fingers.push(f);
  }
}
scene.add(gripper);
const trailGeo = new THREE.BufferGeometry(), trailMat = new THREE.LineBasicMaterial({ color: C.see });
scene.add(new THREE.Line(trailGeo, trailMat));

// --- controls
const controls = new OrbitControls(camera, canvas);
controls.target.copy(W(.03, 0, .24));
Object.assign(controls, { enableDamping: true, minDistance: .4, maxDistance: 2.2, maxPolarAngle: 1.45, enabled: !recorded });
camera.position.copy(W(-.9, -.9, .92));
const user: Shot = { idx: 1, kind: "diverse", which: "many", time: 0, sweep: false };
let t0 = performance.now();
const nIn = el<HTMLInputElement>("n");
nIn.addEventListener("input", () => { user.idx = Number(nIn.value); });
el<HTMLInputElement>("div").addEventListener("change", e => { user.kind = (e.target as HTMLInputElement).checked ? "diverse" : "narrow"; });
el("which").addEventListener("click", e => {
  const w = (e.target as HTMLElement).dataset.w as Which | undefined;
  if (w) { user.which = w; t0 = performance.now(); }
});

// --- chart: log demos vs success
const L = (n: number): number => Math.log10(n);
const chart = new Chart(el("chart"), { x: [L(4.2), L(120)], y: [0, 1], xTicks: COUNTS.map(L), yTicks: [0, .25, .5, .75, 1],
  xFormat: v => String(Math.round(10 ** v)), yFormat: v => `${Math.round(v * 100)}%` }, 760, 300);
const COL: Record<Kind, string> = { diverse: "#34d399", narrow: "#a78bfa" };

function frameAt(t: number): void {
  const s: Shot = recorded ? scripted(t) : { ...user, time: ((performance.now() - t0) / 1000) % 5 };
  const n = COUNTS[s.idx];

  const series = [], bands = [];
  for (const k of ["narrow", "diverse"] as Kind[]) {
    const pts = curve[k], strong = k === s.kind;
    bands.push({ lo: pts.map(p => [L(p.demos), Math.min(...p.runs) / 50] as [number, number]), hi: pts.map(p => [L(p.demos), Math.max(...p.runs) / 50] as [number, number]),
      colour: COL[k], opacity: strong ? .14 : .05 });
    series.push({ points: pts.flatMap(p => p.runs.map(r => [L(p.demos), r / 50] as [number, number])), colour: COL[k], width: 0, dots: true, opacity: strong ? .5 : .15 });
    series.push({ points: pts.map(p => [L(p.demos), p.mean] as [number, number]), colour: COL[k], width: strong ? 3 : 1.6, opacity: strong ? 1 : .45 });
  }
  chart.draw({ series, bands, cursor: L(n) });
  el("count").textContent = tr("{0} 条示范", n);
  for (const [k, id] of [["diverse", "Div"], ["narrow", "Nar"]] as [Kind, string][]) {
    const p = curve[k][s.idx];
    el("v" + id).textContent = `${Math.round(p.mean * 100)}%`;
    el("r" + id).textContent = tr("5 次训练：{0}–{1} / 50", Math.min(...p.runs), Math.max(...p.runs));
    el("b" + id).classList.toggle("on", k === s.kind);
  }
  if (!recorded) el("nVal").textContent = String(n);

  // replay
  const r = replays[s.which], i = clamp(Math.floor(s.time * r.hz), 0, r.gripper.length - 1);
  const g = r.gripper[i], c = r.cup[i];
  gripper.position.copy(W(g[0], g[1], g[2] - .06));
  fingers.forEach((f, j) => { f.position.set((j ? 1 : -1) * (.055 - g[3]), 0, 0); });
  cup.position.copy(W(c[0], c[1], c[2]));
  const failing = !r.success && i > r.hz * 1.2, done = r.success && c[0] > S.bin_xy[0] - S.bin_half && c[2] < S.bin_rim_z;
  const colour = s.sweep ? C.muted : failing ? C.amber : done ? C.see : C.act;
  trailMat.color.set(colour);
  trailGeo.setFromPoints(s.sweep ? [] : r.gripper.slice(0, i + 1).map(p => W(p[0], p[1], p[2] - .03)));
  for (const f of fingers) {
    const m = f.material as THREE.MeshStandardMaterial;
    m.color.set(colour);
    m.emissive.set(colour);
  }
  el("seed").textContent = String(r.seed).padStart(2, "0");
  el("who").innerHTML = `<span style="color:${r.success ? "var(--see)" : "var(--l3)"}">${tr(r.label)}</span>`;
  el("meta").innerHTML = tr("第 {0} s · 夹爪 x {1} m · 杯子高 {2} cm", `<b>${(i / r.hz).toFixed(1)}</b>`, `<b>${g[0] >= 0 ? "+" : "−"}${Math.abs(g[0]).toFixed(2)}</b>`, `<b>${((c[2] - S.table_top - S.cup_hh) * 100).toFixed(1)}</b>`);
  const v = el("verdict");
  if (s.sweep) { v.className = "verdict busy"; v.innerHTML = `▸ SWEEP<small>${tr("拖示范条数，看曲线")}</small>`; }
  else if (done) { v.className = "verdict ok"; v.innerHTML = `✓ ${tr("放进盒子")}<small>${tr("一路直达，没有停顿")}</small>`; }
  else if (failing) { v.className = "verdict warn"; v.innerHTML = `! ${tr("中途跑偏")}<small>${tr("抓起来了，却停在半路，杯子慢慢滑落")}</small>`; }
  else { v.className = "verdict busy"; v.innerHTML = `▸ ${tr("抓取中")}<small>${tr("回放记录下的评测回合")}</small>`; }
  el("src").textContent = tr("数据：L5 毕业项目（MuJoCo {0}，{1}）· 行为克隆 2×64 · 每点 5 次训练 × 50 个起始位置", DATA.env.mujoco, DATA.env.system);

  if (recorded) {
    const az = -2.3 + t * .008, o = controls.target.copy(W(.03, 0, .24));
    camera.position.set(o.x + Math.cos(az) * 1.35, .92, o.z - Math.sin(az) * 1.35);
    camera.lookAt(o);
  } else controls.update();
  render();
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
}

run(frameAt, { dur: Number(q.get("dur") || 17), still: 11 });
